import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { ApiService } from "@core/services/api.service";
import { ITileConfig, RadioOption } from "@shared/components/interfaces/tile.interface";
import { TileTypeNavigationEnum } from "@shared/enums/tile-type-navigation.enum";

@Component({
  selector: 'app-multi-step',
  templateUrl: './multi-step.component.html',
  styleUrls: ['./multi-step.component.scss'],
})
export class MultiStepComponent implements OnInit, OnDestroy {
  currentStep: number = 1;
  selectedRadioValue: string = '';
  tiles: ITileConfig[] = [];
  isLoading: boolean = false;
  private destroy$ = new Subject<void>();

  radioOptions: RadioOption[] = [
    { value: 'explore', label: 'Explore example use cases', method: 'loadMockedTiles' },
    { value: 'lactate', label: 'Create a real lactate test using my data', method: 'loadServerTiles' },
    { value: 'ppd', label: 'Create a real PPD test using my data', method: 'loadCustomTiles' }
  ];

  constructor(
    private router: Router,
    private apiService: ApiService
  ) {}

  ngOnInit(): void {
    this.selectedRadioValue = 'explore';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  navigateToStep(step: number): void {
    this.currentStep = step;
  }

  onGetStarted(): void {
    this.currentStep = 2;
  }

  onContinue(): void {
    if (this.selectedRadioValue) {
      const selectedOption = this.radioOptions.find(option => option.value === this.selectedRadioValue);
      if (selectedOption) {
        this.executeMethod(selectedOption.method);
      }
      this.currentStep = 3;
    }
  }

  onCancel(): void {
    this.currentStep = 1;
  }

  onBackToPrevious(): void {
    this.currentStep = 2;
  }

  private executeMethod(methodName: string): void {
    switch (methodName) {
      case 'loadMockedTiles':
        this.loadMockedTiles();
        break;
      case 'loadServerTiles':
        this.loadServerTiles();
        break;
      case 'loadCustomTiles':
        this.loadCustomTiles();
        break;
      default:
        this.loadMockedTiles();
    }
  }

  loadMockedTiles(): void {
    this.tiles = this.getMockedTiles();
  }

  loadServerTiles(): void {
    this.isLoading = true;
    this.apiService.get('tiles/list', false)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => {
          this.loadMockedTiles();
          return of([]);
        })
      )
      .subscribe((response: ITileConfig[]) => {
        this.tiles = response || this.getMockedTiles();
        this.isLoading = false;
      });
  }

  loadCustomTiles(): void {
    this.tiles = this.getCustomTiles();
  }

  onUseExample(tile: ITileConfig): void {
    this.router.navigate([tile.urlNavigateTo]);
  }

  private getMockedTiles(): ITileConfig[] {
    return [
      {
        id: 1,
        image: 'https://inscyd-prod-public.s3.eu-central-2.amazonaws.com/features/visualization/Visualization.png',
        title: 'Amateur Triathlete',
        description: 'This athlete has balanced endurance and moderate sprint capacity.',
        description_html: '<p>This athlete has balanced endurance and moderate sprint capacity.</p>',
        isRecommended: false,
        buttonName: 'Use the example',
        urlNavigateTo: '',
        tags: [],
        category_name: 'Triathlon',
        tagHeight: 32,
        marketingFeatures: ['Balanced Training', 'Moderate Intensity'],
        suggestedCourses: [],
        price: 0,
        currencyName: 'USD',
        navigationType: TileTypeNavigationEnum.API_CALL,
        configuration: null,
        is_enabled: true
      },
      {
        id: 2,
        image: 'https://inscyd-prod-public.s3.eu-central-2.amazonaws.com/features/visualization/Visualization.png',
        title: 'Professional Cyclist',
        description: 'This athlete has a high VO2max and low VLamax for endurance running.',
        description_html: '<p>This athlete has a high VO2max and low VLamax for endurance running.</p>',
        isRecommended: false,
        buttonName: 'Use the example',
        urlNavigateTo: '',
        tags: [],
        category_name: 'Cycling',
        tagHeight: 32,
        marketingFeatures: ['Professional Training', 'High Performance'],
        suggestedCourses: [],
        price: 0,
        currencyName: 'USD',
        navigationType: TileTypeNavigationEnum.API_CALL,
        configuration: null,
        is_enabled: true
      },
      {
        id: 3,
        image: 'https://inscyd-prod-public.s3.eu-central-2.amazonaws.com/features/visualization/Visualization.png',
        title: 'Masters Age Group Cyclist',
        description: 'This athlete has strong aerobic but shows anaerobic decline with age.',
        description_html: '<p>This athlete has strong aerobic but shows anaerobic decline with age.</p>',
        isRecommended: false,
        buttonName: 'Use the example',
        urlNavigateTo: '',
        tags: [],
        category_name: 'Cycling',
        tagHeight: 32,
        marketingFeatures: ['Age Group Training', 'Aerobic Focus'],
        suggestedCourses: [],
        price: 0,
        currencyName: 'USD',
        navigationType: TileTypeNavigationEnum.API_CALL,
        configuration: null,
        is_enabled: true
      }
    ];
  }

  private getCustomTiles(): ITileConfig[] {
    return [
      {
        id: 4,
        image: 'https://inscyd-prod-public.s3.eu-central-2.amazonaws.com/features/visualization/Visualization.png',
        title: 'Custom Training Plan A',
        description: 'Tailored training for specific performance goals.',
        description_html: '<p>Tailored training for specific performance goals.</p>',
        isRecommended: true,
        buttonName: 'Use the example',
        urlNavigateTo: '/custom-plan-a',
        tags: ['Custom', 'Tailored'],
        category_name: 'Custom',
        tagHeight: 32,
        marketingFeatures: ['Personalized', 'Goal-Oriented'],
        suggestedCourses: [],
        price: 99,
        currencyName: 'USD',
        navigationType: TileTypeNavigationEnum.FEATURE,
        configuration: 1,
        is_enabled: true
      },
      {
        id: 5,
        image: 'https://inscyd-prod-public.s3.eu-central-2.amazonaws.com/features/visualization/Visualization.png',
        title: 'Custom Training Plan B',
        description: 'Advanced custom training methodology.',
        description_html: '<p>Advanced custom training methodology.</p>',
        isRecommended: false,
        buttonName: 'Use the example',
        urlNavigateTo: '/custom-plan-b',
        tags: ['Advanced', 'Custom'],
        category_name: 'Custom',
        tagHeight: 32,
        marketingFeatures: ['Advanced Methods', 'Custom Analysis'],
        suggestedCourses: [],
        price: 149,
        currencyName: 'USD',
        navigationType: TileTypeNavigationEnum.FEATURE,
        configuration: 2,
        is_enabled: true
      }
    ];
  }
}
